import type { ChangeEvent, ReactNode } from 'react'
import { faShield, faShieldCheck } from '@fortawesome/pro-regular-svg-icons'

import CheckBox from '../../CheckBox'
import InputGroup from '../../InputGroup'
import RadioBox from '../../RadioBox'
import { getWarranty } from '../../../utils/uploads'
import type { TNewUploadState } from '../../../hooks/useNewUpload'

type Props = {
  analysedFileTuningTool?: string
  checkout: TNewUploadState['checkout']
  hasIxiSurcharge: boolean
  hasWarranty12: boolean
  hasWarranty24: boolean
  hasWarrantyAddon: boolean
  isDisabled: boolean
  isDtcOnlyCheckoutFlow: boolean
  isManualCheckoutFlow: boolean
  setCheckout: (checkout: TNewUploadState['checkout']) => void
}

const CheckoutAddons = (props: Props) => {
  const {
    analysedFileTuningTool,
    checkout,
    hasIxiSurcharge,
    hasWarranty12,
    hasWarranty24,
    hasWarrantyAddon,
    isDisabled,
    isDtcOnlyCheckoutFlow,
    isManualCheckoutFlow,
    setCheckout,
  } = props

  const handleEcuWarrantyChange = (ecuWarranty: boolean) => {
    setCheckout({
      ...checkout,
      ecuWarranty,
    })
  }

  const handlePriorityChange = () => {
    setCheckout({
      ...checkout,
      priority: !checkout.priority,
    })
  }

  const handleWarrantyRequiredChange = () => {
    setCheckout({
      ...checkout,
      warranty: {
        ...checkout.warranty,
        required: !checkout.warranty.required,
      },
    })
  }

  const handleWarrantyTypeChange = (
    type: TNewUploadState['checkout']['warranty']['type'],
  ) => {
    setCheckout({
      ...checkout,
      warranty: {
        ...checkout.warranty,
        type,
      },
    })
  }

  const handleCustomerNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCheckout({
      ...checkout,
      warranty: {
        ...checkout.warranty,
        customerName: event.target.value,
      },
    })
  }

  const handleCustomerEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCheckout({
      ...checkout,
      warranty: {
        ...checkout.warranty,
        customerEmail: event.target.value,
      },
    })
  }

  const warrantyOptions: ReactNode[] = []

  if (hasWarranty12) {
    warrantyOptions.push(
      <label
        className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 bg-white p-3"
        key="WARRANTY_12"
      >
        <input
          type="radio"
          name="warrantyType"
          checked={checkout.warranty.type === 'WARRANTY_12'}
          onChange={() => handleWarrantyTypeChange('WARRANTY_12')}
        />
        <span className="text-sm font-medium text-black">
          {getWarranty('WARRANTY_12')}
        </span>
      </label>,
    )
  }

  if (hasWarranty24) {
    warrantyOptions.push(
      <label
        className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 bg-white p-3"
        key="WARRANTY_24"
      >
        <input
          type="radio"
          name="warrantyType"
          checked={checkout.warranty.type === 'WARRANTY_24'}
          onChange={() => handleWarrantyTypeChange('WARRANTY_24')}
        />
        <span className="text-sm font-medium text-black">
          {getWarranty('WARRANTY_24')}
        </span>
      </label>,
    )
  }

  const shouldShowIxiFlasherSurcharge =
    !isDtcOnlyCheckoutFlow &&
    hasIxiSurcharge &&
    analysedFileTuningTool === 'IXI_FLASHER'

  return (
    <div className="col-span-5 border-r border-gray-200 bg-gray-50 p-6 sm:col-span-3 md:col-span-2">
      <h3 className="block text-sm font-medium leading-5 text-black">
        Add-ons
      </h3>
      <p className="pb-4 text-sm text-gray-600">
        Add-ons are extra products and services you can purchase to re-sell to
        your customer.
      </p>

      <div className="space-y-3">
        {!isDtcOnlyCheckoutFlow && (
          <RadioBox
            checked={checkout.ecuWarranty}
            checkedLabel={<span className="text-xs">Protect me</span>}
            disabled={isDisabled}
            checkedIcon={faShieldCheck}
            uncheckedIcon={faShield}
            uncheckedLabel={<span className="text-xs">Don't protect me</span>}
            label={<>ECU warranty (0.25 credit)</>}
            description={
              <span className="text-xs">
                Protect yourself with an ECU warranty. If anything goes wrong
                during the writing process TGT will fix or replace the unit
                under this warranty for FREE.
              </span>
            }
            onChange={handleEcuWarrantyChange}
          />
        )}

        {isManualCheckoutFlow && (
          <CheckBox
            checked={checkout.priority}
            disabled={isDisabled}
            label={<>Priority file (0.5 credit)</>}
            description={
              <span className="text-xs">
                Mark this upload as a priority file and receive a faster
                turnaround.
              </span>
            }
            onChange={handlePriorityChange}
          />
        )}

        {!isDtcOnlyCheckoutFlow && hasWarrantyAddon && (
          <CheckBox
            checked={checkout.warranty.required}
            disabled={isDisabled}
            hideDetails={!checkout.warranty.required}
            label={<>Mechanical warranty (from £10.00) </>}
            description={
              <span className="text-xs">
                We recommend you offer mechanical warranty to your customers (UK
                and IE only).
              </span>
            }
            onChange={handleWarrantyRequiredChange}
            renderContent={
              <div className="space-y-4">
                <div className="grid gap-2 sm:grid-cols-1">
                  {warrantyOptions}
                </div>

                <InputGroup
                  required
                  label="Customer name"
                  id="warrantyCustomerName"
                  name="warrantyCustomerName"
                  placeholder="Enter customer's name"
                  disabled={isDisabled}
                  onChange={handleCustomerNameChange}
                  value={checkout.warranty.customerName}
                />

                <InputGroup
                  required
                  label="Customer email"
                  id="warrantyCustomerEmail"
                  name="warrantyCustomerEmail"
                  placeholder="Enter customer's email"
                  disabled={isDisabled}
                  onChange={handleCustomerEmailChange}
                  value={checkout.warranty.customerEmail}
                />

                <div>
                  <p className="text-xs font-semibold uppercase text-black">
                    Important!
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    We will email your customer directly with their warranty
                    information once your file is ready for download. If your
                    upload is re-credited for whatever reason we will refund
                    your warranty payment and the warranty will be void. Refunds
                    may take 2-5 working days from the date your upload is
                    re-credited.
                  </p>
                </div>
              </div>
            }
          />
        )}

        {shouldShowIxiFlasherSurcharge && (
          <div className="rounded-md border border-gray-300 bg-white p-4">
            <p className="text-sm font-medium text-black">
              IXI Flasher surcharge
            </p>
            <p className="mt-1 text-sm text-gray-600">
              This tool requires an additional surcharge which is included
              automatically in the checkout summary.
            </p>
          </div>
        )}

        {isDtcOnlyCheckoutFlow && (
          <p className="text-gray-700 text-xs italic">
            No add-ons are available for DTC-only uploads.
          </p>
        )}
      </div>
    </div>
  )
}

export default CheckoutAddons
